import { ActionDispatching, CaseData, RenderMode, Value } from 'app/types-shared';
import { caseDataToStore } from 'app/infrastructure/store/normalize';
import { createDescriptionSelector } from 'app/infrastructure/translate/index';
import { UiState } from 'app/infrastructure/ui/form/types';
import { SuccessNotification } from 'app/infrastructure/notifications/user-messages';
import {
  caseTypeId as getCaseTypeId, caseNumber as getCaseNumber, primaryKey,
  caseContextId as getCaseContextId
} from 'app/infrastructure/data/wireformat';
import { Domain, UiDefinition } from 'app/infrastructure/ui/types-uidefinition';
import { errorSafe } from 'app/infrastructure/redux';
import { api } from './api';
import { caseListingTranslation } from './constants';
import {
  ReceivePermissions, ReceiveUiDefinitions, RequestCases, ReceiveCases, FailExecuteFavourite,
  RequestRemoveItem, ReceiveRemoveItem, FailRemoveItem, ClearResults, ReceiveRiskMatrixDefinitions,
  RequestUiDefinitions, SetRenderMode, ReplaceCases, ResponseLayoutTypes, RequestLayoutTypes,
  RequestDefaultIncludedFields,
  ResponseDefaultIncludedFields,
  RequestAdditionalIncludedFields,
  ResponseAdditionalIncludedFields,
  UpdateDefaultIncludedFields
} from './action-types';
import { mapRiskMatrixDefinitionToStoreFormat } from 'app/area/case/features/risk-matrix/risk-matrix-definition';
import { actionCreators as responseCachingActionCreators } from 'app/area/shared/response-caching/actions';
import { selectCaseListingInstanceState } from 'app/area/case-report/case-listing/selectors';
import { actionCreators as favouriteActionCreators } from 'app/area/favourite/actions';
import { CaseIncludedField, CaseListingExecuteFavorites } from 'app/area/case-report/case-listing/types';
import { FieldItem } from 'app/area/reports/types';
import { selectCaseTypeIds, selectWorkingDefenitionIncludedDomains } from 'app/area/favourite/selectors';
import uniqBy from 'lodash/uniqBy';
import { fieldValueToFieldItem } from 'app/area/case-report/components/included-domains/map-to-field-items';
import { caseTypeDomainId } from 'app/components/filters/filters-panel/constants';
import { selectIsCaseTypeOptionExcluded } from 'app/area/filters/selectors';

const filterDomainChildren = (domain: Domain, includedDomains: number[]): Domain => {

  const children = (domain.children || [])
    .map(child => filterDomainChildren(child, includedDomains))
    .filter(child =>
      includedDomains.some(id => child.domainId === id)
      || (!!child.children && !!child.children.length));

  return {
    ...domain,
    children: !!children && !!children.length ? children : undefined
  };
};


// eslint:disable-next-line: max-func-body-length
export const actionCreators = (instanceKey: string) => {
  const {
    setWorkingDefinitionLayoutType,
    setWorkingDefinitionIncludedFields
  } = favouriteActionCreators(instanceKey);
  return {
    /**
     * Clears all results in the store.
     */
    clearResults: (): ActionDispatching<any, any> => (dispatch: any, getState: any) => {
      dispatch(new ClearResults(instanceKey));
    },
    
    /**
     * Gets cases permissions and bowtie feature support information for given cases
     *
     * @param ids array of case numbers
     * @param requestId current request id
     */
    getPermissions: (ids: number[], requestId: number = undefined): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {

        const localRequestId = requestId || selectCaseListingInstanceState(instanceKey)(getState())?.requestId;

        const [bowtieSupported] = await Promise.all([          
          api.bowtieSupported(ids)          
        ]);

        const permissions = ids.reduce((result, id) => {
          
          const bowtie = bowtieSupported.find(p => p.caseId === id);
         
          return {
            ...result,
            [id]: {             
              bowtie: !!bowtie && bowtie.supported              
            }
          };
        }, {});

        dispatch(new ReceivePermissions(instanceKey, permissions, localRequestId));
      }),

    /**
     * Gets ui definitions for given case types and filters them by list of included domains
     *
     * @param ids array of case type numbers
     * @param includedDomains list of domains for each case type that should be included
     * @param requestId current request id
     */
    getUiDefinitions: (ids: { caseTypeId: number; caseContextId: number }[],
      includedDomains: { caseTypeId: number; domains: number[] }[],
      requestId: number): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {

        const areaState = selectCaseListingInstanceState(instanceKey)(getState());

        const compareUiDefinitionKey = (a, b) => a.caseTypeId === b.caseTypeId && a.caseContextId === b.caseContextId;

        const uniqueIds = ids.reduce(
          (aggr, i) => !aggr.some(a => compareUiDefinitionKey(a, i)) ? [...aggr, i] : aggr, []
        );

        const uiDefinitionsToGet = uniqueIds.filter(i =>
          !(areaState?.uiDefinitions || []).some(u => compareUiDefinitionKey(u, i))
        );

        if (uiDefinitionsToGet.length === 0) {
          return;
        }

        dispatch(new RequestUiDefinitions(instanceKey, uiDefinitionsToGet, requestId));

        const uiDefinitions = await Promise.all<any>(
          uiDefinitionsToGet.map(
            async uiDefinitionKey => {
              const definition: UiDefinition = await dispatch(
                responseCachingActionCreators.getUiDefinition(uiDefinitionKey.caseTypeId, uiDefinitionKey.caseContextId)
              );

              const includedDomainsForCaseType = includedDomains.find(domains => domains.caseTypeId === uiDefinitionKey.caseTypeId);

              const filteredDomainHierarchy =
                filterDomainChildren(definition.rootDomain, (includedDomainsForCaseType && includedDomainsForCaseType.domains) || []);

              return {
                ...uiDefinitionKey,
                definition: {
                  ...definition,
                  rootDomain: filteredDomainHierarchy
                }
              };
            }
          )
        );

        dispatch(new ReceiveUiDefinitions(instanceKey, uiDefinitions, requestId));
      }),

    /**
     * Gets risk matrix definitions for given case types
     *
     * @param ids array of case type numbers
     * @param requestId current request id
     */
    getRiskMatrixDefinitions: (ids: number[],
      requestId: number): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {

        const areaState = selectCaseListingInstanceState(instanceKey)(getState());

        const uniqueCaseTypes = Array.from(new Set(ids));
        const newCaseTypes = uniqueCaseTypes.filter(id => !areaState?.riskMatrixDefinitions || !areaState?.riskMatrixDefinitions?.[id]);

        if (newCaseTypes.length === 0) {
          return;
        }

        const apiResults = await Promise.all<any>(
          newCaseTypes.map(api.riskMatrixDefinition)
        );

        const riskMatrixDefinitions = apiResults.reduce((result, res) => {
          return {
            ...result,
            [res.caseTypeId]: { fetching: false, definition: mapRiskMatrixDefinitionToStoreFormat(res.definition) }
          };
        }, {});

        dispatch(new ReceiveRiskMatrixDefinitions(instanceKey, riskMatrixDefinitions, requestId));
      }),

    /**
     * Executes search based on the favourite definition
     *
     * @param definition FavouriteDefinition
     * @param loadPermissions If set to true, permissions will be loaded for all cases
     * @param startIndex amount of results from beginning to be skipped
     * @param maxResults how many results should be fetched
     */
    executeFavouriteDefinition: ({
      definition,
      loadPermissions,
      startIndex,
      maxResults = 25,
      replaceResult = false,
      overrideExecuteFavourite
    }: CaseListingExecuteFavorites): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {

        dispatch(new RequestCases(instanceKey));

        const areaState = selectCaseListingInstanceState(instanceKey)(getState());
        const requestId = areaState?.requestId;

        try {
          let response;
          // Dashboard using different APIs to load the data
          if (overrideExecuteFavourite) {
            response = await overrideExecuteFavourite(definition, startIndex, maxResults, true);
          } else {
            response = await api.executeReport(definition, startIndex, maxResults);
          }

          const { results,
            metadata: { domains = [], totalNumberOfResults = 0 } } = response;

          if (!results || !results.length) {
            if (replaceResult) {
              dispatch(new ReplaceCases(instanceKey, {}, [], 0, requestId));
            }
            else {
              dispatch(new ReceiveCases(instanceKey, {}, [], 0, requestId));
            }
            return;
          }

          const toUiDefinitionParams = (r: CaseData) => ({
            caseTypeId: getCaseTypeId(r),
            caseContextId: getCaseContextId(r)
          });
          await Promise.all([
            dispatch(actionCreators(instanceKey).getUiDefinitions(results.map(toUiDefinitionParams), domains, requestId)),
            dispatch(actionCreators(instanceKey).getRiskMatrixDefinitions(results.map(getCaseTypeId), requestId))
          ]);

          const entities = results.reduce((result, caseData: CaseData, i) => ({
            ...result,
            [`${primaryKey(caseData)}-${startIndex + i}`]: caseDataToStore(caseData)
          }), {});

          const caseResults = results.map((caseData: CaseData, i) => ({
            entityId: primaryKey(caseData),
            entityStoreKey: `${primaryKey(caseData)}-${startIndex + i}`,
            caseNumber: getCaseNumber(caseData),
            caseTypeId: getCaseTypeId(caseData),
            caseContextId: getCaseContextId(caseData)
          }));

          if (replaceResult) {
            dispatch(new ReplaceCases(instanceKey, entities, caseResults, totalNumberOfResults, requestId));
          }
          else {
            dispatch(new ReceiveCases(instanceKey, entities, caseResults, totalNumberOfResults, requestId));
          }

          if (loadPermissions) {
            dispatch(actionCreators(instanceKey).getPermissions(results.map(getCaseNumber), requestId));
          }
        }
        catch (error) {
          if (error?.response?.status === 400 && !!error?.userMessages?.length) {
            dispatch(new FailExecuteFavourite(instanceKey, error.userMessages.map(m => m.message)));
          }
        }
      }),

    /**
     * Deletes the case with given case number
     *
     * @param caseNumber
     */
    deleteCase: (caseNumber: number): ActionDispatching<any, any> =>
      async (dispatch: any, getState: any) => {
        dispatch(new RequestRemoveItem(instanceKey, caseNumber));

        try {
          const result = await api.deleteCase(caseNumber) as any;
          const areaState = getState().favourite as UiState;
          const translationDescription = createDescriptionSelector(areaState.texts);

          if (result && result.status === 204) {
            dispatch(new SuccessNotification(
              translationDescription(caseListingTranslation.case_deleted_success)
            ));
            dispatch(new ReceiveRemoveItem(instanceKey, caseNumber));
          }
        } catch (err) {
          dispatch({
            type: 'ERROR',
            error: err
          });

          dispatch(new FailRemoveItem(instanceKey, caseNumber));
        }
      },

    setRenderMode: (renderMode: RenderMode) =>
      (dispatch: any, getState: any) => {
        dispatch(new SetRenderMode(instanceKey, renderMode));
      },

    loadLayoutTypes: (reportId: number): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        if (reportId) {
          dispatch(new RequestLayoutTypes(instanceKey));
          const result = await api.loadLayoutTypes(reportId);
          dispatch(new ResponseLayoutTypes(instanceKey, result.data));
        }
      }),
    setLayoutType: (type: Value): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        dispatch(setWorkingDefinitionLayoutType(type));
      }),

    loadDefaultIncludedFields: (reportId: number): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        if (reportId) {
          dispatch(new RequestDefaultIncludedFields(instanceKey));
          const result = await api.loadDefaultIncludedFields(reportId);
          const includedFields = result?.data?.includedFields?.map((data) => fieldValueToFieldItem(data.domain)) || [];
          const selectedFields = selectWorkingDefenitionIncludedDomains(instanceKey)(getState()) || [];
          const updatedFields = uniqBy(includedFields.concat(selectedFields), 'value');
          dispatch(new ResponseDefaultIncludedFields(instanceKey, updatedFields));
        }
      }),

    loadAdditionalIncludedFields: (reportId: number, query?: string): ActionDispatching<any, any> =>
      errorSafe(async (dispatch: any, getState: any) => {
        if (reportId) {
          dispatch(new RequestAdditionalIncludedFields(instanceKey));
          const caseTypeIds = selectCaseTypeIds(instanceKey)(getState()) || [];
          const applicationExcluded = selectIsCaseTypeOptionExcluded(instanceKey, caseTypeDomainId)(getState());
          const result = await api.loadAdditionalIncludedFields({caseTypeIds,applicationExcluded, query, reportId});
          const includedDomains = result?.data?.map((data) => fieldValueToFieldItem(data.domain, data.children, 
            data.entityTypePath, !!query)) || [];
          dispatch(new ResponseAdditionalIncludedFields(instanceKey, includedDomains));
        }
      }),

    setIncludedFields: (fields: CaseIncludedField[]): ActionDispatching<any, any> => {
      return errorSafe(async (dispatch: any, getState: any) => {
        dispatch(setWorkingDefinitionIncludedFields(fields));
      });
    },

    updateDefaultIncludedFields: (additionalFields: FieldItem[]): ActionDispatching<any, any> => {
      return errorSafe(async (dispatch: any, getState: any) => {
        dispatch(new UpdateDefaultIncludedFields(instanceKey, additionalFields));
      });
    }
  };
};
